import { ByteStream } from './ByteStream';
import { IniSection } from './ini';
import { KnyttPoint } from './KnyttPoint';
import { parseIniInt } from './KnyttUtil';
import type { KnyttWorld } from './KnyttWorld';
import { KnyttWarp } from './KnyttWarp';
import { parseFlag, type Flag } from './JuniValues';

export const AREA_WIDTH = 25;
export const AREA_HEIGHT = 10;
export const AREA_TILE_LAYERS = 4;
export const AREA_SPRITE_LAYERS = 4;
const AREA_CELLS = AREA_WIDTH * AREA_HEIGHT; // 250

export const INT_MIN = -2147483648;

export interface TileLayer {
  tiles: Uint8Array; // length AREA_CELLS
}

export interface ObjectLayer {
  hi: Uint8Array;
  lo: Uint8Array;
}

export type FlagWarpID = 0 | 1 | 2 | 3; // A, B, C, All

export interface FlagWarp {
  flag: Flag | null;
  x: number;
  y: number;
  xArtifactMode: boolean;
  yArtifactMode: boolean;
}

export function getTile(layer: TileLayer, x: number, y: number): number {
  return layer.tiles[y * AREA_WIDTH + x];
}

export function getObjectId(layer: ObjectLayer, x: number, y: number): KnyttPoint {
  const i = y * AREA_WIDTH + x;
  return new KnyttPoint(layer.lo[i], layer.hi[i]);
}

/** A single 25x10 screen. */
export class KnyttArea {
  readonly world: KnyttWorld;
  position: KnyttPoint;
  spoofing: KnyttPoint | null = null;
  get mapPosition(): KnyttPoint {
    return this.spoofing ?? this.position;
  }

  tilesetA = 0;
  tilesetB = 0;
  song = 0;
  atmosphereA = 0;
  atmosphereB = 0;
  background = 0;
  empty = false;

  tileLayers: TileLayer[] = [];
  objectLayers: ObjectLayer[] = [];

  extraData: IniSection | null = null;
  warp: KnyttWarp;
  flagWarps: (FlagWarp | null)[] = [null, null, null, null];

  /** Parse the next area from the map stream, OR construct an empty area at a position. */
  constructor(world: KnyttWorld, source: ByteStream | KnyttPoint) {
    this.world = world;
    this.warp = new KnyttWarp(this);
    if (source instanceof KnyttPoint) {
      this.empty = true;
      this.position = source;
    } else {
      this.position = new KnyttPoint(0, 0);
      this.loadFromStream(source);
    }
    this.fetchAreaExtraData();
    this.fetchFlagWarpData();
  }

  private loadFromStream(map: ByteStream): void {
    this.empty = false;
    this.position = new KnyttPoint(parseNumber(map), parseNumber(map));
    map.skip(4); // misc header

    this.tileLayers = [];
    for (let i = 0; i < AREA_TILE_LAYERS; i++) {
      this.tileLayers.push({ tiles: copyExact(map.readBytes(AREA_CELLS), AREA_CELLS) });
    }
    this.objectLayers = [];
    for (let i = 0; i < AREA_SPRITE_LAYERS; i++) {
      const hi = copyExact(map.readBytes(AREA_CELLS), AREA_CELLS);
      const lo = copyExact(map.readBytes(AREA_CELLS), AREA_CELLS);
      this.objectLayers.push({ hi, lo });
    }

    this.tilesetB = map.readByte();
    this.tilesetA = map.readByte();
    this.atmosphereA = map.readByte();
    this.atmosphereB = map.readByte();
    this.song = map.readByte();
    this.background = map.readByte();
  }

  private fetchAreaExtraData(): void {
    const name = `x${this.position.x}y${this.position.y}`;
    this.extraData = this.world.iniData.getSection(name);
    // MapX/MapY spoofing (MapPanel.cs:114-119): the area is drawn (and visited-
    // marked — setVisited/checkCollectables use MapPosition) at the spoofed cell.
    // C# assigns this in MapPanel.init (i.e. only when the world has a map);
    // assigning at parse time is equivalent wherever the map is reachable.
    const mapX = parseIniInt(this.extraData?.get('MapX'));
    const mapY = parseIniInt(this.extraData?.get('MapY'));
    if (mapX != null || mapY != null) {
      this.spoofing = new KnyttPoint(mapX ?? this.position.x, mapY ?? this.position.y);
    }
  }

  getExtraData(key: string): string | null {
    return this.extraData ? this.extraData.get(key) : null;
  }

  private fetchFlagWarpData(): void {
    if (!this.extraData) return;
    const ids: [FlagWarpID, string][] = [
      [0, 'A'],
      [1, 'B'],
      [2, 'C'],
      [3, 'All'],
    ];
    for (const [id, label] of ids) {
      const flagKey = `Flag(${label})`;
      if (!this.extraData.has(flagKey)) continue;
      const fw: FlagWarp = {
        flag: parseFlag(this.extraData.get(flagKey)),
        x: 0,
        y: 0,
        xArtifactMode: false,
        yArtifactMode: false,
      };
      const parseAxis = (axisKey: string): [number, boolean] => {
        let value = (this.extraData!.get(axisKey) ?? '').toLowerCase();
        let artifact = false;
        if (value.startsWith('artifact')) {
          artifact = true;
          value = value.substring(8);
        }
        return [parseIniInt(value) ?? 0, artifact];
      };
      if (this.extraData.has(`FlagWarpX(${label})`)) {
        const [v, a] = parseAxis(`FlagWarpX(${label})`);
        fw.x = v;
        fw.xArtifactMode = a;
      }
      if (this.extraData.has(`FlagWarpY(${label})`)) {
        const [v, a] = parseAxis(`FlagWarpY(${label})`);
        fw.y = v;
        fw.yArtifactMode = a;
      }
      this.flagWarps[id] = fw;
    }
  }

  toString(): string {
    return `[Area ${this.position}]`;
  }
}

/** Reads digits/sign/dot, consuming the terminator byte. Returns INT_MIN if a '.' appears. */
function parseNumber(map: ByteStream): number {
  let s = '';
  let c = map.readByte();
  while ((c >= 48 && c < 58) || c === 45 || c === 46) {
    s += String.fromCharCode(c);
    c = map.readByte();
  }
  if (s.includes('.')) return INT_MIN;
  if (s === '' || s === '-') return INT_MIN;
  return parseInt(s, 10);
}

/** Ensure a fixed-length byte array (pad with zeros if the stream was short). */
function copyExact(src: Uint8Array, len: number): Uint8Array {
  if (src.length === len) return new Uint8Array(src);
  const out = new Uint8Array(len);
  out.set(src.subarray(0, len));
  return out;
}
